import { useCallback, useEffect, useRef, useState } from 'react';
import { formatFileSize } from '../helpers/fileHelper';

const getFileKey = file => `${file.name}_${file.size}_${file.lastModified}`;

const revokePreviewIfNeeded = fileItem => {
    if (fileItem?.previewSrc) {
        URL.revokeObjectURL(fileItem.previewSrc);
    }
};

const mapPendingFile = file => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    return {
        file,
        extension: file.name.split('.').pop()?.toUpperCase() || '',
        formattedSize: formatFileSize(file.size),
        previewSrc: (isImage || isVideo) ? URL.createObjectURL(file) : null,
        mediaType: isImage ? 'image' : isVideo ? 'video' : 'doc',
    };
};

export const usePendingFiles = () => {
    const [files, setFiles] = useState([]);
    const filesRef = useRef([]);

    useEffect(() => {
        filesRef.current = files;
    }, [files]);

    const onFilesSelected = useCallback(event => {
        const selectedFiles = Array.from(event.target.files || []);
        if (!selectedFiles.length) {
            return;
        }

        setFiles(prevFiles => {
            const existingKeys = new Set(prevFiles.map(item => getFileKey(item.file)));
            const uniqueNewItems = selectedFiles
                .filter(file => {
                    const key = getFileKey(file);
                    if (existingKeys.has(key)) {
                        return false;
                    }

                    existingKeys.add(key);
                    return true;
                })
                .map(mapPendingFile);

            return [...prevFiles, ...uniqueNewItems];
        });

        event.target.value = "";
    }, []);

    const removeFile = useCallback(index => {
        setFiles(prevFiles => {
            const fileItem = prevFiles[index];
            revokePreviewIfNeeded(fileItem);
            return prevFiles.filter((_, i) => i !== index);
        });
    }, []);

    const clearFiles = useCallback(() => {
        setFiles(prevFiles => {
            prevFiles.forEach(revokePreviewIfNeeded);
            return [];
        });
    }, []);

    useEffect(() => () => {
        filesRef.current.forEach(revokePreviewIfNeeded);
    }, []);

    return {
        files,
        onFilesSelected,
        removeFile,
        clearFiles,
    };
};
